import React, { useEffect, useMemo } from 'react';
import { equals, prop, length, propEq, includes, filter, reduce, isEmpty, find, compose, propOr } from 'ramda';
import { mapIndexed } from 'ramda-adjunct';
import moment from 'moment';
import { Button, Dropdown, Input, Pagination, Select, Table } from 'antd';
import { DeleteOutlined, EditOutlined, MailOutlined, MoreOutlined, PlusOutlined, SearchOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { ROLES } from '../../lib/rights';
import withUser from '../../withUser';
import { QOffersQuery } from '../../_graphql/queries/QOffers';
import useFetchQuery from '../../hooks/useFetchQuery';
import UserModal from '../../pages/administration/components/users/userForm/UserModal';
import DeleteUserModal from '../../pages/administration/components/users/DeleteUserModal';
import useUserFilterOptions from '../../hooks/useUserFilterOptions';
import { QUsersQuery } from '../../_graphql/queries/users/QUsers';
import useUserTableData from '../../hooks/useUserTableData';
import useUserTableActions from '../../hooks/useUserTableActions';
import { PAGE_SIZE_OPTIONS } from '../../_CONST';
import ModalCreateTeam from '../../pages/team/modals/ModalCreateTeam';
import EllipsisTooltip from '../EllipsisTooltip';
import { ARCHIVED_LABEL_SUFFIX, RoleLabelEnum } from './utils/roles';
import RoleTag from './RoleTag';

const UsersList = ({ userTeam, user: actualUser, type, itemDisabled, teams, userRefetch }) => {
  const isAdministration = equals('administration', type);
  const teamId = prop('teamId', userTeam);

  const { users, isLoading, reFetch } = useFetchQuery({
    query: QUsersQuery,
    dataProp: 'users',
    defaultData: []
  });

  const { offers, isLoading: isLoadingOffers } = useFetchQuery({
    query: QOffersQuery,
    args: { filter: { allTeams: isAdministration } },
    dataProp: 'offers',
    defaultData: []
  });

  const {
    options,
    optionsToDisplay,
    roles
  } = useUserFilterOptions(teams, offers, isAdministration);

  const usersOnList = useMemo(() => {
    if (isAdministration) return users;

    return reduce((acc, user) => {
      const teamRoles = filter(
        ({ teamId: currentTeamId }) => equals(currentTeamId, teamId),
        prop('teamRoles', user) || []
      );

      if (isEmpty(teamRoles)) return acc;

      acc.push({
        ...user,
        teamRoles
      });
      return acc;
    }, [], users);
  }, [isAdministration, users, teamId]);

  const {
    pagination,
    onChangePagination,
    search,
    setSearch,
    selectedItem,
    setSelectedItem,
    selectedRole,
    setSelectedRole,
    filteredUsers,
    usersDataSource,
    teamsUsers
  } = useUserTableData(usersOnList, isAdministration, options, offers, teamId);

  useEffect(() => {
    if (itemDisabled(selectedRole)) {
      setSelectedItem(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, itemDisabled]);

  const {
    userModalIsOpen,
    deleteModalIsOpen,
    createTeamModalIsOpen,
    selectedUser,
    handleCloseModal,
    onAddUser,
    onEditUser,
    onRemoveUser,
    onResendInvitation,
    onConfirmDelete,
    selectUserByEmail,
    setCreateTeamModalIsOpen,
    onCreateTeam,
    loading
  } = useUserTableActions({
    reFetch,
    isAdministration,
    teamId,
    userRefetch
  });

  const baseTable = (key, width) => ({
    dataIndex: key,
    key,
    width,
    showSorterTooltip: false
  });

  const firstSelectDefaultValue = isAdministration ? RoleLabelEnum.ALL_TEAMS : RoleLabelEnum.ALL_OFFERS;

  const columns = [
    { 
      title: 'Utilisateur',
      ...baseTable('email', 300),
      render: (email, { index, rowSpan, user }) => {
        const userIsPending = prop('isPending', user);
        return {
          children: <div className="flex items-center gap-1 min-w-0 whitespace-nowrap">
            <div className="min-w-0 w-full max-w-[250px]">
              <EllipsisTooltip text={email} tooltipClassName="min-w-0 max-w-[250px]" forceBreakWords />
            </div>
            {userIsPending && (
              <span className="text-flexi-warning shrink-0 rounded-full bg-flexi-warning-toaster px-2 ml-2">
                en attente
              </span>
            )}
          </div>,
          props: { rowSpan: equals(0, index) ? rowSpan : 0 }
        };
      } 
    },
    {
      title: isAdministration ? 'Équipes' : 'Offres',
      ...baseTable('item', 250),
      render: (item, { isArchived }) => <span className="text-flexi-grey">{isArchived ? `${item} ${ARCHIVED_LABEL_SUFFIX}`: item}</span>
    },
    {
      title: 'Rôles',
      ...baseTable('roles', 300),
      render: (roles, { user, item, rowSpan }) => {
        const userId = prop('id', user);
        const itemId = compose(
          prop('id'),
          find(propEq(item, 'name'))
        )(teams);
        const nbOfferOnTeam = compose(
          length,
          reduce((acc, offer) => {
            const offerTeamId = prop('team', offer);
            const offerName = prop('offerName', offer);
            if (!equals(offerTeamId, itemId) || includes(offerName, acc)) return acc;
            return [...acc, offerName];
          }, [])
        )(offers);
        return (
          <div className="flex flex-col gap-2">
            {mapIndexed(({ archivedCount, count, label, color, value }, index) => {
              const isWithoutRoles = equals(label, RoleLabelEnum.WITHOUT_ROLES);
              const isOnAllOffers = equals(count, nbOfferOnTeam);
              const offersInfo = !isWithoutRoles && isAdministration
                ? includes(value, [ROLES.admin, ROLES.superAdmin]) || isOnAllOffers
                  ? `(${RoleLabelEnum.ALL_OFFERS})`
                  : `(${count} offre${count > 1 ? 's' : ''})`
                : undefined;
              return (
                <RoleTag
                  key={`${userId}-${value}-${index}`}
                  label={label}
                  offersInfo={offersInfo}
                  color={color}
                  archivedCount={archivedCount}
                />
              );
            })(roles)}
          </div>
        );
      }
    },
    {
      title: 'Date d\'ajout',
      ...baseTable('signUp', 100),
      render: (signUp, { user, index, rowSpan }) => {
        const isPending = prop('isPending', user);
        return {
          children: isPending ? RoleLabelEnum.PENDING: moment(signUp).format('L'),
          props: { rowSpan: equals(index, 0) ? rowSpan : 0 }
        };
      }
    },
    {
      title: 'Actions',
      width: 50,
      render: ({ user, index, rowSpan }) => {
        if (equals(prop('id', user), prop('userId', actualUser))) return null;
        return {
          children: (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 1,
                    label: 'Modifier les rôles',
                    icon: <EditOutlined />,
                    onClick: () => onEditUser(user),
                    disabled: propEq('pending', 'status', user)
                  },
                  prop('isPending', user) && {
                    key: 2,
                    label: 'Renvoyer l\'invitation',
                    icon: <MailOutlined />,
                    onClick: () => onResendInvitation(prop('email', user))
                  },
                  {
                    key: 3,
                    label: <span className="text-flexi-error">{isAdministration ? 'Supprimer' : 'Retirer'} l'{prop('isPending', user) ? 'invitation' : 'utilisateur'}</span>,
                    icon: <DeleteOutlined className="!text-flexi-error" />,
                    onClick: () => onRemoveUser(user)
                  }
                ]
              }}
            >
              <Button className="border-0 !bg-transparent shadow-none" icon={<MoreOutlined />} />
            </Dropdown>
          ),
          props: { rowSpan: index === 0 ? rowSpan: 0 }
        };
      }
    }
  ];

  return (
    <>
      <div className="flex flex-col bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex justify-between  flex-wrap mb-4 gap-2">
          <div className="flex gap-2">
            <Input
              className="!w-[400px]" placeholder="Recherchez un utilisateur" prefix={<SearchOutlined />}
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              allowClear
              className="!w-[200px]"
              showSearch
              optionFilterProp="label"
              options={optionsToDisplay}
              value={selectedItem || firstSelectDefaultValue}
              onChange={setSelectedItem}
              disabled={itemDisabled(selectedRole)}
            />
            <Select
              allowClear
              className="!w-[200px]"
              options={roles}
              value={selectedRole || RoleLabelEnum.ALL_ROLES}
              onChange={setSelectedRole}
            />
            {isAdministration && <Button icon={<PlusOutlined />} onClick={() => setCreateTeamModalIsOpen(true)}>Créer une nouvelle équipe</Button>}
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAddUser}>Ajouter un utilisateur</Button>
        </div>

        <Table
          className="no-hover-table"
          rowClassName={prop('rowClassName')}
          columns={columns}
          dataSource={usersDataSource}
          loading={isLoading || isLoadingOffers}
          pagination={false}
          locale={{
            emptyText: <div className="flex flex-col align-center">
              <div className="flex justify-center">
                <UsergroupAddOutlined className="bg-flexi-info-toaster text-flexi-primary-color p-2 !rounded-full" />
              </div>
              <span className="text-black">Aucune donnée</span>
              <span>Modifiez les filtres ou ajoutez des utilisateurs dans l'équipe sélectionnée</span>
            </div> 
          }}
        />
        <div className="flex justify-end mt-3">
          <Pagination
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            total={length(filteredUsers)}
            current={prop('current', pagination)}
            pageSize={prop('pageSize', pagination)}
            onChange={(current, pageSize) => onChangePagination({ current, pageSize }, { currentDataSource: usersDataSource })}
          />
        </div>
      </div>

      <UserModal
        open={userModalIsOpen} 
        handleClose={handleCloseModal} 
        selectedUser={selectedUser}
        teams={teams}
        offers={offers}
        users={users}
        selectUserByEmail={selectUserByEmail(teamsUsers)}
        isAdministration={isAdministration}
        defaultTeamId={teamId}
      />
      {isAdministration && (
        <ModalCreateTeam
          visible={createTeamModalIsOpen}
          onCancel={() => setCreateTeamModalIsOpen(false)}
          onCreateTeam={onCreateTeam}
          teams={teams}
          loadingModal={propOr(false, `create-team-${teamId}`, loading)}
        />
      )}
      <DeleteUserModal
        open={deleteModalIsOpen}
        handleClose={handleCloseModal}
        user={selectedUser}
        teams={teams}
        offers={offers}
        onConfirm={onConfirmDelete}
        isAdministration={isAdministration}
        loadingModal={propOr(false, `delete-user-${prop('id', selectedUser)}`, loading)}
      />
    </>
  );
};

export default withUser(UsersList);